import React from 'react';
import PropTypes from 'prop-types';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { Stack } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { TreeItem2 } from '@mui/x-tree-view/TreeItem2';
import { treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { useTreeViewApiRef } from '@mui/x-tree-view/hooks';
import { SearchV2 } from 'components';
import { useIntl } from 'react-intl';
import { _ } from 'utils/libHelper';

function getItemDescendantsIds(item) {
	const ids = [];
	(item?.originalChildren ?? item.children)?.forEach((child) => {
		ids.push(child.id);
		ids.push(...getItemDescendantsIds(child));
	});

	return ids;
}

// const CustomTreeItem = styled(TreeItem2, {
// 	shouldForwardProp: (prop) => prop !== 'hasSelect',
// })(({ theme, hasSelect }) => ({
const CustomTreeItem = styled(TreeItem2)(({ theme }) => ({
	color: theme.palette.grey[200],
	[`& .${treeItemClasses.content}`]: {
		borderRadius: theme.spacing(0.5),
		padding: theme.spacing(0.5, 1),
		margin: theme.spacing(0.2, 0),
		[`& .${treeItemClasses.label}`]: {
			fontSize: '0.8rem',
			fontWeight: 500,
		},
	},
	[`& .${treeItemClasses.iconContainer}`]: {
		borderRadius: '50%',
		backgroundColor: theme.palette.primary.dark,
		padding: theme.spacing(0, 1.2),
		...theme.applyStyles('light', {
			backgroundColor: alpha(theme.palette.primary.main, 0.25),
		}),
		...theme.applyStyles('dark', {
			color: theme.palette.primary.contrastText,
		}),
	},
	[`& .${treeItemClasses.groupTransition}`]: {
		marginLeft: 15,
		paddingLeft: 18,
		borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
	},
	...theme.applyStyles('light', {
		color: theme.palette.grey[800],
	}),
}));

/**
 * Recursively filters a tree structure, keeping only nodes whose name contains the filterString.
 *
 * @param {Array<Object>} treeData - The tree data array.
 * @param {string} filterString - The string to filter by (case-insensitive).
 * @param {Function} [getItemLabel] - A function to get the label of an item.
 * @returns {Array<Object>} - The filtered tree data.
 */
function searchTree(treeData = [], filterString, getItemLabel = undefined) {
	return treeData.reduce((filteredTree, node) => {
		const filteredChildren = searchTree(node.children, filterString, getItemLabel);

		const nodeLabel = getItemLabel ? getItemLabel(node) : node.label;
		const nodeMatches = nodeLabel.toLowerCase().includes(filterString.toLowerCase());
		const childrenMatch = filteredChildren.length > 0;

		if (nodeMatches || childrenMatch) {
			filteredTree.push({
				...node,
				originalChildren: node.children,
				children: nodeMatches ? node.children : filteredChildren,
			});
		}

		return filteredTree;
	}, []);
}

export function MUIRichTreeView({
	treeItems,
	enableSingleSelection = false,
	enableMultiSelection = false,
	parentChildrenSelection = true,
	disableChildrenIfParentisSelected = false,
	enableSearch = false,
	selectedItemIds = [],
	setSelectedItemIds = () => null,
	disabledItemIds = [],
	setDisabledItemIds = () => null,
	isAnyChildrenSelected,
	getItemId,
	getItemLabel,
	...rest
}) {
	// if (parentChildrenSelection && enableSingleSelection) {
	// 	// reset enableSingleSelection to false
	// 	enableSingleSelection = false;
	// }
	const intl = useIntl();
	const toggledItemRef = React.useRef({});
	const apiRef = useTreeViewApiRef();
	const [searchValue, setSearchValue] = React.useState('');

	const filteredTreeItems = React.useMemo(() => {
		return searchValue ? searchTree(treeItems, searchValue, getItemLabel) : treeItems;
	}, [getItemLabel, searchValue, treeItems]);

	const throttleSetSearchText = React.useMemo(
		() => _.throttle((val) => setSearchValue(val), 500, { leading: false, trailing: true }),
		[]
	);

	const handleItemSelectionToggle = React.useCallback((event, itemId, isSelected) => {
		if (itemId) toggledItemRef.current[itemId] = isSelected;
	}, []);

	const handleSelectedItemsChange = React.useCallback(
		(event, newSelectedItemIds) => {
			if (!Array.isArray(newSelectedItemIds)) newSelectedItemIds = [newSelectedItemIds];
			if (!parentChildrenSelection && !disableChildrenIfParentisSelected) {
				return setSelectedItemIds(newSelectedItemIds);
			}

			// Select / unselect (or disable/enable) the children of the toggled item
			const childrenItemIdsToSelect = [];
			const childrenItemsToUnSelect = {};
			Object.entries(toggledItemRef.current).forEach(([itemId, isSelected]) => {
				const item = apiRef.current.getItem(itemId);
				const descendants = getItemDescendantsIds(item);
				if (isSelected) {
					childrenItemIdsToSelect.push(...descendants);
				} else {
					descendants.forEach((descendantId) => {
						childrenItemsToUnSelect[descendantId] = true;
					});
				}
			});
			toggledItemRef.current = {};

			if (parentChildrenSelection) {
				const newSelectedItemIdsWithChildren = Array.from(
					new Set(
						[...newSelectedItemIds, ...childrenItemIdsToSelect].filter(
							(itemId) => !childrenItemsToUnSelect[itemId]
						)
					)
				);

				setSelectedItemIds(newSelectedItemIdsWithChildren);
			} else {
				// Note: in case the selected item is a parent, all its chiledren (which may be selected previously) should be unselected
				//	if it is an unselected parent, its children should be unselected previously, so no need to unselect them again
				const childrenMapper = {};
				childrenItemIdsToSelect.forEach((descendantId) => {
					childrenMapper[descendantId] = true;
				});
				setSelectedItemIds(newSelectedItemIds.filter((itemId) => !childrenMapper[itemId]));
			}

			if (disableChildrenIfParentisSelected) {
				const newDisabledChildrenItemIds = Array.from(
					new Set(
						[...disabledItemIds, ...childrenItemIdsToSelect].filter(
							(itemId) => !childrenItemsToUnSelect[itemId]
						)
					)
				);
				setDisabledItemIds(newDisabledChildrenItemIds);
			}
		},
		[
			apiRef,
			disableChildrenIfParentisSelected,
			disabledItemIds,
			parentChildrenSelection,
			setDisabledItemIds,
			setSelectedItemIds,
		]
	);
	return (
		<Stack direction="column" spacing={0.5}>
			{enableSearch && (
				<SearchV2
					boxSx={{
						width: '100%',
						display: 'flex',
						pt: 1,
						pl: 1,
						pr: 1,
						alignItems: 'flex-start',
					}}
					value={searchValue}
					showSearchBtn={false}
					placeholder={intl.formatMessage({ id: 'pages.shareable.searchInputPlaceholder' })}
					onChange={(e) => {
						throttleSetSearchText(e.target.value);
					}}
					onClearClick={() => setSearchValue('')}
				/>
			)}
			<RichTreeView
				sx={{ p: 1, minWidth: 350 }}
				checkboxSelection={enableSingleSelection || enableMultiSelection}
				multiSelect={enableMultiSelection}
				expansionTrigger="iconContainer"
				items={filteredTreeItems}
				getItemId={getItemId}
				getItemLabel={getItemLabel}
				slots={{ item: CustomTreeItem }}
				slotProps={
					(enableSingleSelection || enableMultiSelection) && isAnyChildrenSelected
						? {
								item: (ownerState) => {
									return {
										slotProps: {
											content: {
												sx: (theme) => {
													return isAnyChildrenSelected(ownerState.itemId)
														? {
																backgroundColor: alpha(theme.palette.primary.main, 0.25),
																'&:hover': {
																	backgroundColor: alpha(theme.palette.primary.main, 0.45),
																},
														  }
														: {};
												},
											},
										},
									};
								},
						  }
						: undefined
				}
				apiRef={apiRef}
				selectedItems={
					enableSingleSelection
						? selectedItemIds[0] ?? ''
						: enableMultiSelection
						? selectedItemIds
						: undefined
				}
				onSelectedItemsChange={
					enableSingleSelection || enableMultiSelection ? handleSelectedItemsChange : undefined
				}
				onItemSelectionToggle={
					enableSingleSelection || enableMultiSelection ? handleItemSelectionToggle : undefined
				}
				isItemDisabled={(item) => disabledItemIds.includes(item.id)}
				{...rest}
			/>
		</Stack>
	);
}

MUIRichTreeView.propTypes = {
	treeItems: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
			children: PropTypes.array,
		})
	).isRequired,
	// If true, selecting a parent will select all its children
	// Note: if it is true, it implicitly means multiple selection,
	//			do not set both parentChildrenSelection & enableSingleSelection to true at same time
	parentChildrenSelection: PropTypes.bool,
	// If true, only one item can be selected at a time
	// Note: if enableMultiSelection is true, this will be ignored
	enableSingleSelection: PropTypes.bool,
	enableMultiSelection: PropTypes.bool,
	disableChildrenIfParentisSelected: PropTypes.bool,
	enableSearch: PropTypes.bool,
	selectedItemIds: PropTypes.arrayOf(PropTypes.string),
	setSelectedItemIds: PropTypes.func,
	disabledItemIds: PropTypes.arrayOf(PropTypes.string),
	setDisabledItemIds: PropTypes.func,
	isAnyChildrenSelected: PropTypes.func,
	getItemId: PropTypes.func, // get the id of an item if the property name is not "id"
	getItemLabel: PropTypes.func, // get label of an item if the property name is not "label"
};
